/**
 * fechasDelActa — las zonas de fecha se editan con un calendario, no a mano.
 *
 * Una casilla de texto libre para una fecha admite «11/sep/2026», «11-09-26»,
 * «11 de sept.», «mañana» y el dedo que se equivoca de tecla. Todas acaban
 * impresas en el acta tal cual, y la de dentro del documento es la que vale.
 *
 * Con un `<input type="date">` la fecha se elige de un calendario, el navegador
 * la enseña en el formato del sistema de cada quien y lo que se guarda no
 * depende de cómo la teclee nadie.
 *
 * ── Lo que NO hace: imponer un formato ──────────────────────────────────
 *
 * El acta es de quien la escribió. Si su plantilla dice «11/sep/2026», se
 * devuelve «11/sep/2026»; si dice «11 de septiembre de 2026», se devuelve eso.
 * Se reconoce la forma que ya tenía el campo y se vuelve a escribir igual. Solo
 * cuando el campo está vacío y no hay forma que imitar se usa la larga, que es
 * la que produce el generador a partir de la cita del calendario.
 *
 * Y si el valor no se puede entender como fecha —porque dice «por definir», o
 * «la semana del 3»— la zona se queda como caja de texto. Cambiarla por un
 * calendario habría borrado de la pantalla lo que ponía, que es la peor forma
 * de "ayudar".
 */

const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
];

// Lo que escribe la gente de verdad: con y sin punto, «sept» y «set», y
// «setiembre», que es correcto en buena parte de América.
const ABREVIATURAS = {
  ene: 1, feb: 2, mar: 3, abr: 4, may: 5, jun: 6,
  jul: 7, ago: 8, sep: 9, sept: 9, set: 9, oct: 10, nov: 11, dic: 12
};

const sinTildes = (t) => String(t || '')
  .toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

const dosCifras = (n) => String(n).padStart(2, '0');

/** El número de mes a partir de su nombre o abreviatura. 0 si no se reconoce. */
function mesDe(palabra) {
  const limpia = sinTildes(palabra).replace(/\.$/, '');
  if (!limpia) return 0;
  const largo = MESES.indexOf(limpia === 'setiembre' ? 'septiembre' : limpia);
  if (largo >= 0) return largo + 1;
  return ABREVIATURAS[limpia] || 0;
}

/**
 * ¿Esta etiqueta de la plantilla pide una fecha?
 *
 * «Hora» queda fuera aunque acompañe a «fecha» —«Fecha y hora»— porque un
 * selector de fecha se comería la hora al guardar.
 */
export function esEtiquetaDeFecha(etiqueta) {
  const texto = sinTildes(etiqueta);
  if (!texto) return false;
  if (/\bhora/.test(texto)) return false;
  return /(fecha|dia\b|vence|vencimiento|plazo|limite|resolucion|caduca)/.test(texto);
}

/**
 * ¿Esta etiqueta pide una hora?
 *
 * «Hora Programada», «Hora Inicio», «Hora de término». Se tratan aparte de las
 * fechas porque el control del navegador es otro —`type="time"`— y porque
 * confundirlos borraría el dato: un selector de fecha sobre «Hora Inicio»
 * guardaría un día donde había un reloj.
 */
export function esEtiquetaDeHora(etiqueta) {
  const texto = sinTildes(etiqueta);
  if (!texto) return false;
  // «Fecha y hora» lleva las dos cosas: no es ni un campo de fecha ni uno de
  // hora, y cualquiera de los dos controles se comería la mitad del dato.
  if (/fecha/.test(texto)) return false;
  return /(\bhora\b|horario|\bhrs?\b)/.test(texto);
}

/**
 * La hora escrita en el acta, en el formato de 24 h que pide el control.
 *
 * Devuelve '' si no se entiende, y entonces la zona se queda como texto.
 */
export function aHora24(texto) {
  const t = sinTildes(texto).trim().replace(/\s+/g, ' ');
  if (!t) return '';

  const m = t.match(/^(\d{1,2})[:.](\d{2})\s*(a\.?m\.?|p\.?m\.?|am|pm)?$/);
  if (!m) return '';

  let horas = Number(m[1]);
  const minutos = Number(m[2]);
  const sufijo = (m[3] || '').replace(/\./g, '');

  if (minutos > 59) return '';

  if (sufijo) {
    if (horas < 1 || horas > 12) return '';
    if (sufijo === 'pm' && horas !== 12) horas += 12;
    if (sufijo === 'am' && horas === 12) horas = 0;
  } else if (horas > 23) {
    return '';
  }

  return `${dosCifras(horas)}:${dosCifras(minutos)}`;
}

/**
 * De vuelta al acta.
 *
 * Aquí no se imita el formato anterior como con las fechas: una hora en el acta
 * se escribe «10:30» y punto, y devolver «10:30 a.m.» obligaría a decidir entre
 * «a.m.», «AM» y «a. m.» sin que ninguna aporte nada sobre las 24 horas.
 */
export function desdeHora24(hora) {
  return /^\d{2}:\d{2}$/.test(String(hora || '')) ? hora : '';
}

/** Qué forma tiene la fecha que ya estaba escrita, para devolverla igual. */
export function formaDe(texto) {
  const t = sinTildes(texto).trim();
  if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(t)) return 'iso';
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(t)) return 'numerica';
  if (/^\d{1,2}\/[a-z]+\.?\/\d{4}$/.test(t)) return 'corta';
  return 'larga';
}

/**
 * La fecha escrita en el acta, como la quiere `<input type="date">`.
 *
 * Devuelve '' cuando el texto no es una fecha reconocible: quien llama lo
 * entiende como "esto no es una fecha, déjalo en texto".
 */
export function aISO(texto) {
  const t = sinTildes(texto).trim().replace(/\s+/g, ' ');
  if (!t) return '';

  let dia = 0, mes = 0, anio = 0;
  let m;

  if ((m = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/))) {
    [anio, mes, dia] = [Number(m[1]), Number(m[2]), Number(m[3])];
  } else if ((m = t.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/))) {
    // Día primero: es lo que escribe quien redacta estas actas, y lo que
    // produce el propio generador. Un 03/04 es el 3 de abril.
    [dia, mes, anio] = [Number(m[1]), Number(m[2]), Number(m[3])];
  } else if ((m = t.match(/^(\d{1,2})[\s/-]+(?:de\s+)?([a-z.]+)[\s/-]+(?:de\s+|del\s+)?(\d{4})$/))) {
    [dia, anio] = [Number(m[1]), Number(m[3])];
    mes = mesDe(m[2]);
  } else {
    return '';
  }

  if (!mes || mes < 1 || mes > 12 || !dia || dia < 1 || dia > 31) return '';
  if (anio < 1900 || anio > 2999) return '';

  // Que el día exista: un 31 de febrero se teclea más de lo que parece, y
  // aceptarlo dejaría en el acta una fecha que no ocurrió nunca.
  const prueba = new Date(anio, mes - 1, dia);
  if (prueba.getMonth() !== mes - 1 || prueba.getDate() !== dia) return '';

  return `${anio}-${dosCifras(mes)}-${dosCifras(dia)}`;
}

/** De vuelta al acta, con la misma forma que tenía antes. */
export function desdeISO(iso, forma = 'larga') {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '';

  const [anio, mes, dia] = [Number(m[1]), Number(m[2]), Number(m[3])];

  if (forma === 'iso') return iso;
  if (forma === 'numerica') return `${dosCifras(dia)}/${dosCifras(mes)}/${anio}`;
  if (forma === 'corta') {
    const abreviado = MESES[mes - 1].slice(0, 3);
    return `${dia}/${abreviado}/${anio}`;
  }
  return `${dia} de ${MESES[mes - 1]} de ${anio}`;
}
